import { useId, useMemo, useState } from 'react';
import { ConfirmDialog } from '../../job-roles/components/ConfirmDialog';
import { healthcareErrorMessage } from '../healthcareErrors';
import { healthcareRatesGateway } from '../index';
import { HEALTHCARE_TERM_HINTS, industryTagLabel, matchesSuggestion, payBasisLabels, type ServiceType } from '../types';
import { ServiceTypeDialog } from './ServiceTypeDialog';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

export interface ServiceTypesSectionProps {
  /** Every work type including inactive ones; this section filters what it shows. */
  serviceTypes: ServiceType[];
  /** The industry tags suggested for the company, and the company's primary industry in words (null when none is chosen). */
  tags: string[];
  industryLabel: string | null;
  defaultTags: string[];
  healthcare: boolean;
  canManage: boolean;
  /** Re-reads the catalog after a change. */
  onChanged: () => void;
  /** Lets the page open the add dialog from its action bar: each change of the number opens it once. */
  addRequest?: number | undefined;
}

/**
 * Work types (called service/visit types in healthcare): EPN's defaults (read-only) and the company's own (add, edit, deactivate, reactivate). Inactive ones
 * are hidden from pickers but keep their history. The list is narrowed to what is suggested for the company's industry until the person ticks the box.
 */
export function ServiceTypesSection({ serviceTypes, tags, industryLabel, defaultTags, healthcare, canManage, onChanged, addRequest }: ServiceTypesSectionProps) {
  const uid = useId();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [mine, setMine] = useState(tags.length > 0);
  const [dialog, setDialog] = useState<{ editing?: ServiceType } | null>(null);
  const [lastRequest, setLastRequest] = useState(addRequest ?? 0);
  const [confirming, setConfirming] = useState<ServiceType | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The page's "Add Work Type" button asks this section to open its dialog (derived from props during render, not an effect).
  if ((addRequest ?? 0) !== lastRequest) {
    setLastRequest(addRequest ?? 0);
    if (canManage) setDialog({});
  }

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return serviceTypes
      .filter((t) => (showInactive || t.isActive)
        && (!(mine && tags.length > 0) || !t.isSystem || matchesSuggestion(t.industries, tags))
        && (!q || t.name.toLowerCase().includes(q) || t.code.toLowerCase().includes(q) || t.description.toLowerCase().includes(q)))
      .sort((a, b) => Number(b.isSystem) - Number(a.isSystem) || a.name.localeCompare(b.name));
  }, [serviceTypes, search, showInactive, mine, tags]);

  async function reactivate(t: ServiceType) {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await healthcareRatesGateway.reactivateServiceType(t.id, t.concurrencyVersion);
      setStatus(`${t.name} was reactivated.`);
      onChanged();
    } catch (err) {
      setError(healthcareErrorMessage(err, 'Could not reactivate the work type.'));
    } finally {
      setBusy(false);
    }
  }

  async function deactivate(t: ServiceType) {
    setBusy(true);
    setConfirmError(null);
    try {
      await healthcareRatesGateway.deactivateServiceType(t.id, t.concurrencyVersion);
      setConfirming(null);
      setStatus(`${t.name} was deactivated. Existing rates and past payroll still show it.`);
      onChanged();
    } catch (err) {
      setConfirmError(healthcareErrorMessage(err, 'Could not deactivate the work type.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="hdc-section" aria-labelledby={`${uid}-title`} id="work-types">
      <div className="section-header">
        <h3 id={`${uid}-title`}>Work types</h3>
        {canManage && (
          <button type="button" className="jr-btn jr-btn--primary" onClick={() => { setDialog({}); setStatus(null); }}><span aria-hidden="true">+ </span>Add work type</button>
        )}
      </div>
      <p className="jr-hint">
        The work performed: for example a service call, a night shift, a delivery, a project, a class or a patient visit. EPN&apos;s defaults are starting points for you to
        configure; they are not legal or payer determinations, and they cannot be changed here. Add your own for anything missing.
        {healthcare ? ` ${HEALTHCARE_TERM_HINTS.workType}` : ''}
      </p>
      {!canManage && <p className="jr-readonly" role="note">You can see work types but not change them.</p>}
      {status && <p className="jr-ok" role="status">{status}</p>}
      {error && <p className="jr-error jr-error--block" role="alert">{error}</p>}

      <div className="jr-toolbar">
        <div className="jr-field jr-field--search">
          <label htmlFor={`${uid}-search`}>Search work types</label>
          <input id={`${uid}-search`} type="search" placeholder="Name, code or description" value={search} onChange={(e) => setSearch(e.target.value)} autoComplete="off" />
        </div>
        <div className="jr-filters" role="group" aria-label="Work type filters">
          {tags.length > 0 && industryLabel && (
            <label className="hdc-check">
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
              <span>Only defaults suggested for {industryLabel}</span>
            </label>
          )}
          <label className="hdc-check">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            <span>Show inactive</span>
          </label>
        </div>
      </div>

      <div className="jr-table-scroll" role="region" aria-label="Work types (scrolls sideways on a narrow screen)" tabIndex={0}>
        <table className="jr-table" aria-label="Work types">
          <thead>
            <tr>
              <th scope="col">Name</th><th scope="col">Code</th><th scope="col">Source</th><th scope="col">Suggested for</th>
              <th scope="col">Suggested pay basis</th><th scope="col">Status</th><th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="muted">No work types match.</td></tr>}
            {rows.map((t) => (
              <tr key={t.id}>
                <th scope="row">{t.name}</th>
                <td>{t.code}</td>
                <td>{t.isSystem ? <span className="hdc-badge">EPN default</span> : <span className="hdc-badge hdc-badge--ok">Your company</span>}</td>
                <td>{t.industries.length === 0 ? 'Any' : t.industries.map(industryTagLabel).join(', ')}</td>
                <td>{t.suggestedPayBasis ? payBasisLabels[t.suggestedPayBasis] : 'None'}</td>
                <td><span className={`hdc-badge${t.isActive ? ' hdc-badge--ok' : ''}`}>{t.isActive ? 'Active' : 'Inactive'}</span></td>
                <td>
                  {t.isSystem ? (
                    <span className="muted">Read-only</span>
                  ) : canManage ? (
                    <div className="jr-actions">
                      <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" aria-label={`Edit ${t.name}`} disabled={busy} onClick={() => { setDialog({ editing: t }); setStatus(null); }}>Edit</button>
                      {t.isActive ? (
                        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" aria-label={`Deactivate ${t.name}`} disabled={busy} onClick={() => { setConfirming(t); setConfirmError(null); }}>Deactivate</button>
                      ) : (
                        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" aria-label={`Reactivate ${t.name}`} disabled={busy} onClick={() => void reactivate(t)}>Reactivate</button>
                      )}
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialog && (
        <ServiceTypeDialog
          editing={dialog.editing}
          defaultTags={defaultTags}
          industryLabel={industryLabel}
          healthcare={healthcare}
          onClose={() => setDialog(null)}
          onSaved={(saved) => { setDialog(null); setStatus(`${saved.name} was ${dialog.editing ? 'updated' : 'added'}.`); onChanged(); }}
        />
      )}
      {confirming && (
        <ConfirmDialog
          title="Deactivate work type"
          confirmLabel="Deactivate"
          busy={busy}
          error={confirmError}
          onCancel={() => setConfirming(null)}
          onConfirm={() => void deactivate(confirming)}
        >
          <p>
            Deactivate <strong>{confirming.name}</strong>? It will no longer be offered when you add rates or pay-run lines. Existing rates and past payroll keep showing it, and you
            can reactivate it later.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
